// 시장 이슈 · 기업 정보 패널 — 관련 기업을 고르면 1년 주가와 기본 정보를 보여준다.
//
// 배치는 화면 폭이 정한다(사용자 요구: PC 는 화면 이동 없이, 좁으면 헷갈리지 않게):
//   · 넓은 데스크톱: 상세 오른쪽에 4번째 열로 선다(원래 3열 설계의 '관련 기업' 자리)
//   · 좁은 데스크톱(≤1120px): 상세를 누르지 않고 오른쪽에서 덮는 오버레이
//   · 모바일(≤760px): 이슈 시트 **위로** 올라오는 바텀 시트(그래버·딤·스와이프 닫기)
// 셸·전환은 market.css 의 .mk-aside 블록이 맡는다.
//
// 데이터는 이미 있던 서버 엔드포인트를 그대로 쓴다:
//   · 기본 정보 = /agent/market/company (이 패널을 위해 만들어졌던 것 — c968bd7 에서
//     프론트만 걷어냈다)
//   · 주가 = vachart price 위젯이 /agent/stock-price 를 스스로 받는다(기간 전환·비교
//     포함). MarketPage 가 setFetcher 로 인증 fetch 를 주입해 둔다.
import { useEffect, useRef, useState } from "react";
import { authHeaders } from "../lib/api";
import { chartHtml } from "../lib/markdown";
import { VAChart } from "../lib/vachart";
import { CompanyLogo } from "../ui/CompanyLogo";
import { SheetGrabber } from "../ui/Sheet";
import type { MkCompany } from "./MarketPage";

/** V2 심볼 표기 — 접두가 이미 붙어 오는 종목이 있다(KRX:015760, news_trend 실측).
 *  무조건 exchange 를 붙이면 KRX:KRX:015760 이 되어 시세 조회가 조용히 실패한다. */
export function fullSym(c: { symbol?: string; exchange?: string }): string {
  const s = String(c.symbol || "").trim();
  if (!s) return "";
  return s.includes(":") ? s : `${c.exchange || "KRX"}:${s}`;
}

/** 주가·기본 정보를 보여줄 수 있는 기업인가 — 심볼이 없거나(매칭 실패·비상장)
 *  NICE 심볼(비상장 DB)이면 시세가 없다. server.py 의 상장 판정과 같은 규칙. */
export function isListed(c: { symbol?: string; exchange?: string }): boolean {
  const s = fullSym(c);
  return !!s && !/^NICE:/i.test(s);
}

/** 기본 정보 — 서버 /agent/market/company 가 화면이 쓸 것만 골라 내려준다 */
interface CoMeta {
  name?: string;
  symbol?: string;
  exchange?: string;
  listedDate?: string;
  businessArea?: string;
  overview?: string;
  marketCap?: number;
  per?: number;
  pbr?: number;
  roe?: number;
  tags?: string[];
}

// 같은 종목을 다시 골라도 재요청하지 않는다. null = 조회했지만 정보가 없던 종목.
// 네트워크 실패는 담지 않는다 — 다시 고르면 재시도된다.
const metaCache = new Map<string, CoMeta | null>();

// 패널 너비 — 사용자가 왼쪽 엣지를 끌어 조절하고 브라우저에 남긴다(목록 폭과 같은 방식)
const CO_W_KEY = "va_market_co_w";
const CO_W_MIN = 320;
const CO_W_MAX = 560;
const CO_W_DEFAULT = 360;

function savedCoWidth(): number {
  const v = Number(localStorage.getItem(CO_W_KEY) || 0);
  return v >= CO_W_MIN && v <= CO_W_MAX ? v : CO_W_DEFAULT;
}

/** 시가총액 — 조/억 단위로 줄인다(원문은 원 단위 실수) */
function moneyKo(v?: number): string {
  if (typeof v !== "number" || !isFinite(v) || v <= 0) return "—";
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}조원`;
  if (v >= 1e8) return `${Math.round(v / 1e8).toLocaleString("ko-KR")}억원`;
  return `${Math.round(v).toLocaleString("ko-KR")}원`;
}
const ratio = (v?: number): string =>
  typeof v === "number" && isFinite(v) ? v.toFixed(2) : "—";
const pctOf = (v?: number): string =>
  typeof v === "number" && isFinite(v) ? `${v.toFixed(1)}%` : "—";

/** 업종 — V2 가 콤마로 이어 붙인 목록으로 주는 기업이 있다(카카오: "인터넷
 *  서비스(인터넷 광고)/데이타베이스,인터넷관련 소프트웨어,…호스팅" 실측).
 *  그대로 두면 우측 정렬 네 줄이 되어 읽기 어렵다 — 첫 항목 + 외 N 으로 줄이고
 *  전체는 title(툴팁)로 남긴다. */
function briefArea(s: string): string {
  const parts = s.split(",").map((t) => t.trim()).filter(Boolean);
  return parts.length > 1 ? `${parts[0]} 외 ${parts.length - 1}` : s;
}

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export interface MarketCompanyPanelProps {
  co: MkCompany | null;
  /** 상세가 이미 받아 둔 전일 대비 — 없으면(문서 탭) 자리를 비운다 */
  chg?: { close: number; pct: number };
  onClose: () => void;
}

export default function MarketCompanyPanel({ co, chg, onClose }: MarketCompanyPanelProps) {
  const sym = co ? fullSym(co) : "";
  const [meta, setMeta] = useState<CoMeta | null>(null);
  const [metaState, setMetaState] = useState<"loading" | "done" | "none">("none");
  const boxRef = useRef<HTMLDivElement | null>(null);
  const finRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState<number>(savedCoWidth);
  const [dragging, setDragging] = useState(false);

  /** 너비 끌기 — 왼쪽 엣지. 왼쪽으로 끌면 넓어진다(목록 그립과 부호 반대).
   *  문서 전체에 리스너를 걸어 커서가 손잡이를 벗어나도 드래그가 이어진다. */
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const start = w;
    setDragging(true);
    document.body.classList.add("mk-resizing");
    const move = (ev: MouseEvent) =>
      setW(Math.min(CO_W_MAX, Math.max(CO_W_MIN, start + (startX - ev.clientX))));
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.classList.remove("mk-resizing");
      setDragging(false);
      setW((v) => {
        try {
          localStorage.setItem(CO_W_KEY, String(v));
        } catch {
          /* noop */
        }
        return v;
      });
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  const resetWidth = () => {
    setW(CO_W_DEFAULT);
    try {
      localStorage.setItem(CO_W_KEY, String(CO_W_DEFAULT));
    } catch {
      /* noop */
    }
  };

  // Esc 로 닫는다 — 패널이 열려 있을 때만 리스너를 단다
  useEffect(() => {
    if (!co) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [co, onClose]);

  // 기본 정보 — 고를 때만 받는다(스냅샷에 담으면 이슈 수 × 기업 수만큼 배치가 커진다)
  useEffect(() => {
    if (!sym) return;
    if (metaCache.has(sym)) {
      const m = metaCache.get(sym) || null;
      setMeta(m);
      setMetaState(m ? "done" : "none");
      return;
    }
    let alive = true;
    setMeta(null);
    setMetaState("loading");
    (async () => {
      try {
        const r = await fetch(`/agent/market/company?symbol=${encodeURIComponent(sym)}`, {
          headers: authHeaders(),
        });
        const d = (await r.json()) as { ok?: boolean; data?: CoMeta };
        const m = d?.ok && d.data ? d.data : null;
        metaCache.set(sym, m);
        if (alive) {
          setMeta(m);
          setMetaState(m ? "done" : "none");
        }
      } catch {
        /* 기본 정보는 보조다 — 주가 차트만 남는다 */
        if (alive) setMetaState("none");
      }
    })();
    return () => {
      alive = false;
    };
  }, [sym]);

  // 주가 차트 — 채팅과 같은 price 위젯(.chart-box → hydrate). 심볼만 주면 위젯이
  // 1년치(250일)를 스스로 받고, 기간·일주월·캔들 전환과 비교 추가까지 그대로 된다.
  // <article> 래퍼가 hydrate 의 '한 scope 에 차트 하나' 생존 규칙을 이 패널로 가둔다
  // — 없으면 scope 가 document.body 가 되어 다른 화면의 차트와 서로 지운다.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    VAChart.dispose(box);
    box.innerHTML = sym
      ? chartHtml({ type: "price", symbol: sym, name: co?.name || sym, days: 250, height: 240 })
      : "";
    if (sym) VAChart.hydrate(box);
    return () => VAChart.dispose(box);
  }, [sym, co?.name]);

  // 최근 재무 — 채팅과 같은 financials 위젯(연간/분기·연수 전환, 매출·순이익 표 포함).
  // 주가와 별도 <article> 이라 hydrate 생존 규칙이 서로를 지우지 않는다.
  useEffect(() => {
    const box = finRef.current;
    if (!box) return;
    VAChart.dispose(box);
    box.innerHTML = sym
      ? chartHtml({ type: "financials", symbol: sym, name: co?.name || sym, period: "year", years: 3, height: 190 })
      : "";
    if (sym) VAChart.hydrate(box);
    return () => VAChart.dispose(box);
  }, [sym, co?.name]);

  const dir = chg ? (chg.pct > 0 ? "up" : chg.pct < 0 ? "down" : "") : "";

  return (
    <aside className="mk-aside" aria-label="기업 정보" style={{ width: w }}>
      <SheetGrabber className="mk-grab" sheetSelector=".mk-aside" onClose={onClose} />
      {/* 왼쪽 엣지 너비 손잡이 — 목록 그립과 같은 문법(호버 시 파란 라인). 모바일엔 없다 */}
      <button
        type="button"
        className={`mk-aside-rz${dragging ? " dragging" : ""}`}
        title="너비 조절 (두 번 클릭하면 기본값)"
        aria-label="기업 패널 너비 조절"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
      />
      {co && (
        <>
          <div className="mk-aside-head">
            <CompanyLogo symbol={co.symbol} name={co.name} size="lg" />
            <div className="mk-aside-name">
              <b>{co.name}</b>
              <span>{sym}</span>
            </div>
            {chg && (
              <span className={`mk-aside-chg ${dir}`}>
                {`${chg.pct > 0 ? "+" : ""}${chg.pct.toFixed(1)}%`}
              </span>
            )}
            <button type="button" className="mk-aside-x" aria-label="기업 정보 닫기" onClick={onClose}>
              <svg viewBox="0 0 24 24" {...stroke}>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div className="mk-aside-scroll">
            <div className="mk-sec">
              <div className="mk-sec-h">주가 추이</div>
              <article className="mk-aside-chart">
                <div ref={boxRef} />
              </article>
            </div>

            {/* 최근 재무 — meta 블록 밖이다: 조건부로 감싸면 meta 도착 타이밍에 ref 가
                비어 위젯이 영영 안 그려지고, 재무는 기본 정보와 소스도 다르다 */}
            <div className="mk-sec">
              <div className="mk-sec-h">최근 재무</div>
              <article className="mk-aside-chart mk-aside-fin">
                <div ref={finRef} />
              </article>
            </div>

            {metaState === "loading" && <div className="mk-none">기업 정보를 불러오는 중…</div>}
            {metaState === "none" && (
              <div className="mk-none">기업 기본 정보를 찾지 못했습니다. 주가·재무만 표시합니다.</div>
            )}
            {/* 텍스트 정보(기본 지표·기업 정보)는 같은 문법 — 라벨·값 한 줄씩 직렬.
                섹션 순서 = 주가·재무(차트) → 지표 → 개요 → 정보 → 테마 (Bishop 지정) */}
            {meta && (
              <>
                <div className="mk-sec">
                  <div className="mk-sec-h">기본 지표</div>
                  <dl className="mk-aside-rows">
                    <div><dt>시가총액</dt><dd>{moneyKo(meta.marketCap)}</dd></div>
                    <div><dt>PER</dt><dd>{ratio(meta.per)}</dd></div>
                    <div><dt>PBR</dt><dd>{ratio(meta.pbr)}</dd></div>
                    <div><dt>ROE</dt><dd>{pctOf(meta.roe)}</dd></div>
                  </dl>
                </div>

                {meta.overview && (
                  <div className="mk-sec">
                    <div className="mk-sec-h">기업 개요</div>
                    <p className="mk-aside-ov">{meta.overview}</p>
                  </div>
                )}

                {(meta.businessArea || meta.listedDate) && (
                  <div className="mk-sec">
                    <div className="mk-sec-h">기업 정보</div>
                    <dl className="mk-aside-rows">
                      {meta.businessArea && (
                        <div><dt>업종</dt><dd title={meta.businessArea}>{briefArea(meta.businessArea)}</dd></div>
                      )}
                      {meta.listedDate && <div><dt>상장일</dt><dd>{meta.listedDate}</dd></div>}
                    </dl>
                  </div>
                )}

                {!!meta.tags?.length && (
                  <div className="mk-sec">
                    <div className="mk-sec-h">투자 테마</div>
                    <div className="mk-aside-tags">
                      {meta.tags.map((t) => <span key={t} className="mk-aside-tag">{t}</span>)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
